import { reportsAgentEn } from './reportsAgent';
import { step5En } from './step5';
// English locale — the source of truth for the key list. Other locales (it.js)
// fill in the same keys; any they omit falls back to these English strings.
//
// Coverage note (intentionally scoped): nav + Home + Settings are covered in
// full; Dashboard / Cases / Inventory / Schedule cover the visible chrome
// (titles, tab/section labels, column headers, buttons, empty/loading states).
// Deeply dynamic, data-derived sentences are migrated incrementally and still
// render correctly in English until then.
const en = {
  // ── language switcher ──
  'lang.it': 'Italian',
  'lang.en': 'English',

  // ── nav (AppHeader) ──
  'nav.home': 'Home',
  'nav.dashboard': 'Dashboard',
  'nav.cases': 'Cases',
  'nav.inventory': 'Inventory',
  'nav.schedule': 'Schedule',
  'nav.reports': 'Reports',
  'production.title': 'Production',
  'production.enable': 'Configure production (add a recipe)',
  'nav.voice': 'Voice',
  'nav.readyToOrder': '{n} ready to order',

  // ── Home ──
  'home.greeting': 'Overview',
  'home.sub.loading': 'Loading',
  'home.sub.caughtUp': 'No items require attention',
  'home.sub.attentionOne': '1 item requires attention',
  'home.sub.attentionMany': '{n} items require attention',
  'home.settings': 'Settings',
  'home.askHint': 'Ask a question or issue a command',
  'home.tile.cases': 'Cases',
  'home.tile.schedule': 'Schedule',
  'home.tile.dashboard': 'Dashboard',
  'home.tile.reports': 'Reports',
  'home.tile.inventory': 'Inventory',
  'home.tile.cases.sub': 'Invoices for review',
  'home.tile.cases.badge': '{n} for review',
  'home.tile.schedule.sub': 'Scheduled items',
  'home.tile.schedule.overdue': '{n} overdue',
  'home.tile.dashboard.sub': 'Performance',
  'home.tile.reports.sub': 'Financial summary',
  'home.tile.reports.sub.agent': 'Commission & analytics',
  'home.tile.cases.count': '{n} for review',
  'home.tile.inventory.count': '{n} items in stock',
  'home.tile.schedule.count': '{n} coming up',
  'home.tile.inventory.sub': 'Stock on hand',
  'home.tile.inventory.low': '{n} below reorder point',
  'home.value.title': 'Exceptions identified',
  'home.value.caught': 'recovered on supplier invoices',
  'home.value.billChecked': 'invoice checked',
  'home.value.billsChecked': 'invoices checked',
  'home.value.lowOne': 'item below reorder point',
  'home.value.lowMany': 'items below reorder point',

  // ── Settings ──
  'settings.title': 'Settings',
  'settings.subtitle': 'Account and configuration.',
  'settings.comingSoon': 'Coming soon',
  'settings.section.account': 'Account',
  'settings.section.team': 'Team members',
  'settings.section.admin': 'Floxir admin',
  'settings.section.email': 'Email',
  'settings.section.notifications': 'Notifications',
  'settings.section.autoReorder': 'Auto-reorder',
  'settings.section.billing': 'Billing',
  'settings.account.company': 'Company',
  'settings.account.email': 'Your email',
  'settings.account.empty': 'Company and account details appear once signed in.',
  'settings.signOut': 'Sign out',
  'settings.signingOut': 'Signing out…',
  'settings.team.invite': 'Invite a colleague to this workspace. They will receive an email to set a password and will have access to the same company data.',
  'settings.team.placeholder': 'colleague@yourcompany.it',
  'settings.team.send': 'Send invite',
  'settings.team.sending': 'Sending…',
  'settings.team.enterEmail': 'Enter an email address.',
  'settings.team.onlyAdmin': 'Only an owner or admin can invite colleagues.',
  'settings.admin.create': 'Create a new customer',
  'settings.admin.createHelp': 'Create a company workspace and invite its owner by email.',
  'settings.email.note': 'Supplier correspondence is sent from this address:',
  'settings.email.sentFrom': 'Sent from',
  'settings.email.loadError': 'The sender address could not be loaded.',
  'settings.email.none': 'No sender address configured.',
  'settings.email.later': 'Sending from your own address, and routing supplier replies, can be configured later.',
  'settings.notifications.banner': 'Show a banner when Floxir prepares orders',
  'settings.notifications.bannerHelp': 'Displays a banner when prepared orders await approval.',
  'settings.autoReorder.manage': 'Manage auto-reorder rules',
  'settings.autoReorder.manageHelp': 'Configure automatic reordering, reorder points and quantities.',
  'settings.billing.note': 'Plan and billing history.',

  // ── Dashboard (visible chrome) ──
  'dash.filter.week': 'This week',
  'dash.filter.month': 'This month',
  'dash.filter.quarter': 'This quarter',
  'dash.filter.year': 'This year',
  'dash.loading': 'Loading…',
  'dash.error': 'The dashboard could not be loaded: {msg}',
  'dash.empty': 'No orders, invoices or cases on file. Figures appear once documents have been processed.',
  'dash.badge.healthy': 'Healthy',
  'dash.badge.attention': 'Needs attention',
  'dash.section.insights': 'Insights',
  'dash.section.money': 'Money',
  'dash.section.atWork': 'Processing',
  'dash.section.trends': 'Trends',
  'dash.card.stillToPay': 'Still to pay',
  // Agent variants. A broker never spends the deal value — it passes between the
  // mill and the buyer — so the buy-side wording above describes someone else's
  // money. Same figures, named from the right side of the trade.
  'dash.card.stillToPay.agent': 'Awaiting payment',
  'dash.card.caught': 'Exceptions identified',
  'dash.card.moneyOverTime': 'Exceptions over time',
  'dash.card.last6': 'Last 6 months',
  'dash.card.whereMoney': 'Spend by supplier',
  'dash.card.whereMoney.agent': 'Deal value by supplier',
  'dash.card.topSuppliers': 'Top suppliers by spend',
  'dash.card.topSuppliers.agent': 'Top suppliers by deal value',
  'dash.card.upcoming': 'Upcoming payments',
  'dash.card.upcoming.agent': 'Upcoming due dates',
  'dash.card.suppliers': 'Suppliers',
  'dash.stat.docsRead': 'Documents read',
  'dash.stat.docsReadSub': 'orders, delivery notes and invoices',
  'dash.stat.cleared': 'Cleared automatically',
  'dash.stat.errors': 'Invoices with errors',

  // ── Cases (visible chrome) ──
  'cases.loading': 'Loading…',
  'cases.addDocument': 'Add a document',
  'cases.photoOrUpload': 'Photo or file',
  'cases.justArrived': 'Just arrived',
  'cases.reading': 'Processing documents…',
  'cases.seeHistory': 'Document history',
  'cases.matched': 'Matched',

  // ── Inventory (visible chrome) ──
  'inv.scan': 'Scan',
  'inv.searchPlaceholder': 'Search items',
  'inv.allSuppliers': 'All suppliers',
  'inv.col.item': 'Item',
  'inv.col.supplier': 'Supplier',
  'inv.col.inStock': 'In stock',
  'inv.col.onOrder': 'On order',
  'inv.col.received': 'Received',
  'inv.col.paid': 'Paid',
  'inv.col.status': 'Status',
  'inv.section.products': 'Products',
  'inv.productsInStock': 'Products in stock',
  'inv.stockValue': 'Stock value',
  'inv.section.raw': 'Raw materials',
  'inv.section.finished': 'Finished products',
  'inv.loading': 'Loading…',
  'inv.empty': 'No inventory items.',
  'inv.noMatch': 'No items match the selected filters.',

  // ── Schedule (visible chrome) ──
  'sched.overdue': 'Overdue',
  'sched.thisWeek': 'This week',
  'sched.nextWeek': 'Next week',
  'sched.chase': 'Send follow-up',
  'sched.to': 'To',
  'sched.subject': 'Subject',
  'sched.messageItalian': 'Message',
  'sched.expected': 'Expected',

  // ── New today (Home tile rows and the Cases list a row opens) ──
  'needs.title': 'New today',
  'needs.row.price_above_order': 'Price above order',
  'needs.row.duplicate_invoice': 'Duplicate invoice',
  'needs.row.order_missing': 'Order missing',
  'needs.row.documents_overdue': 'Documents overdue',
  'needs.row.vat_error': 'VAT error',
  'needs.new': '{n} new',
  'needs.unavailable': '–',
  'needs.filter.today': 'New today',
  'needs.filter.open': 'All open',
  'needs.showOpen': 'Show all open of this type',
  'needs.showToday': 'Show only what is new today',
  'needs.back': 'Cases',
  'needs.error': 'This list could not be loaded. Nothing is shown rather than a list that does not match the number.',
  'needs.missingSome': '{n} of these cases are not shown on this page.',
  'needs.empty': 'Nothing here.',

  // ── Redesigned header and Home ──
  'header.upload': 'Upload',
  'header.ask': 'Ask',
  'header.search': 'Search',
  'header.settings': 'Settings',
  'header.searchSoon': 'Search is coming soon',
  'header.close': 'Close',
  'header.mainMenu': 'Main',
  'header.company': 'Company',
  'header.conversation': 'Conversation in progress',
  'home.askByVoice': 'Ask by voice',
  'biz.agent': 'Agent',
  'biz.distributor': 'Distributor',
  'biz.manufacturer': 'Manufacturer',
  'biz.hybrid': 'Hybrid',
  'settings.language': 'Language',

  // ── Search (bar under the header and the results page) ──
  'search.placeholder': 'Search a PO number, invoice number, supplier or buyer',
  'search.closeLabel': 'Close search',
  'search.tooShort': 'Minimum 3 characters',
  // ── Cases lists for agents (redesign step 3, piece 4) ──
  'cases.list.back': 'Back to Cases',
  'cases.list.companies.all': 'All companies',
  'cases.list.companies.one': '1 company',
  'cases.list.companies.many': '{n} companies',
  'cases.list.sortAndDates': 'Sort and dates',
  'cases.list.sort.recent': 'Most recent',
  'cases.list.sort.value': 'Most value',
  'cases.list.sort.due': 'Soonest due',
  'cases.list.sort.custom': 'Custom',
  'cases.list.dates.from': 'From',
  'cases.list.dates.to': 'to',
  'cases.list.dates.invalid': 'The first date must not be after the second.',
  'cases.list.tabs': 'Types',
  'cases.list.tab.attention.all': 'All',
  'cases.list.tab.attention.price': 'Price above order',
  'cases.list.tab.attention.quantity': 'Quantity differs',
  'cases.list.tab.attention.charge_not_ordered': 'Charge not ordered',
  'cases.list.tab.attention.missing_line': 'Missing line item',
  'cases.list.tab.attention.documents_late': 'Documents late',
  'cases.list.tab.attention.duplicate': 'Duplicate invoice',
  'cases.list.tab.attention.order_missing': 'Order missing',
  'cases.list.tab.attention.confirmation_differs': 'Confirmation differs',
  'cases.list.tab.attention.delivery_vs_packing': 'Delivery note vs packing list',
  'cases.list.tab.open.all': 'All',
  'cases.list.tab.open.invoice': 'Waiting for invoice',
  'cases.list.tab.open.delivery': 'Waiting for delivery',
  'cases.list.tab.open.not_confirmed': 'Not confirmed',
  'cases.list.tab.matched.all': 'All',
  'cases.list.tab.matched.internal_po': 'Internal PO',
  'cases.list.tab.done.all': 'All',
  'cases.list.chip.order': 'Order',
  'cases.list.chip.confirmation': 'Confirmation',
  'cases.list.chip.delivery': 'Delivery note',
  'cases.list.chip.invoice': 'Invoice',
  'cases.list.assumed': 'assumed',
  'cases.list.chip.paid': 'Paid',
  'cases.list.chip.packing_list': 'Packing list',
  'cases.list.chip.due': 'Due {date}',
  'cases.list.col.order': 'Order',
  'cases.list.col.companies': 'Companies',
  'cases.list.col.have': 'Documents',
  'cases.list.col.waitingFor': 'Waiting for',
  'cases.list.col.since': 'Since',
  'cases.list.col.waiting': 'Waiting',
  'cases.list.col.due': 'Due',
  'cases.list.col.matched': 'Documents',
  'cases.list.col.recovered': 'Recovered',
  'cases.list.col.doneDate': 'Done date',
  'cases.list.weak': "Floxir isn't sure this invoice belongs to this order.",
  'cases.list.om.requestAll': 'Request all from',
  'cases.list.bar.invoices': 'Request invoices from',
  'cases.list.bar.orders': 'Request orders from',
  'cases.list.late.weeks': 'Invoice missing · {n} weeks since PO',
  'cases.list.late.days': 'Invoice missing · {n} days since PO',
  'cases.list.late.week': 'Invoice missing · 1 week since PO',
  'cases.list.late.day': 'Invoice missing · 1 day since PO',
  'cases.list.internalPo': 'Internal PO · invoice {n}',
  'cases.list.om.more.one': 'and 1 more supplier',
  'cases.list.om.more.many': 'and {n} more suppliers',
  'cases.list.om.named': 'Invoice {n} ({date}) names this order, but it never arrived',
  'cases.list.om.namedNoDate': 'Invoice {n} names this order, but it never arrived',
  'cases.list.om.noInvoice': 'An invoice names this order, but it never arrived',
  'cases.list.om.noRef': 'No order number on the invoice',
  'cases.list.dup': 'Same invoice {n} received twice',
  'cases.list.dup.generic': 'Duplicate of an earlier invoice',
  'cases.list.reason.price': 'Invoice price above the agreed order',
  'cases.list.reason.quantity': 'Quantity differs between the documents',
  'cases.list.reason.billed_over_delivered': 'Billed for more than was delivered',
  'cases.list.reason.charge_not_ordered': 'Charge billed, never on the order',
  'cases.list.reason.missing_line': 'Not on order',
  'cases.list.reason.vat_error': 'VAT does not add up',
  'cases.list.reason.other': 'Needs a look',
  'cases.list.units': '{n} units',
  'cases.list.daysOpen.one': '1 day open',
  'cases.list.daysOpen.many': '{n} days open',
  'cases.list.days.one': '1 day',
  'cases.list.days.many': '{n} days',
  'cases.list.waiting.invoice': 'Invoice',
  'cases.list.waiting.delivery': 'Delivery',
  'cases.list.waiting.deliveryNote': 'Delivery note',
  'cases.list.due.today': 'Today',
  'cases.list.due.in': 'in {n} days',
  'cases.list.due.late': '{n} days overdue',
  'cases.list.done.one': '1 case · {money} recovered',
  'cases.list.done.many': '{n} cases · {money} recovered',
  'cases.list.loading': 'Loading…',
  'cases.list.error': "The cases can't be loaded right now.",
  'cases.list.summaryError': "The counts can't be loaded right now.",
  'cases.list.empty': 'No cases',
  'cases.list.empty.notBuilt': "This check isn't built yet, so there is nothing to show.",
  'cases.list.loadingMore': 'Loading more…',
  'cases.list.moreError': "More cases couldn't be loaded.",
  'cases.list.showMore': 'Show more',
  // ── Cases hub for agents (redesign step 3) ──
  'cases.hub.title': 'Cases',
  'cases.hub.attention': 'Needs attention',
  'cases.hub.open': 'Open',
  'cases.hub.matched': 'Matched',
  'cases.hub.done': 'Done',
  'cases.hub.history': 'History',
  'cases.hub.late': 'Documents late {n}',
  'cases.hub.otherCurrency.one': '+ 1 case without an exchange rate',
  'cases.hub.otherCurrency.many': '+ {n} cases without an exchange rate',
  'cases.hub.error': "The case numbers can't be loaded right now.",
  'cases.hub.retry': 'Try again',
  'cases.hub.filter.group': 'Companies',
  'cases.hub.filter.all': 'All companies',
  'cases.hub.filter.choose': 'Choose companies ▾',
  'cases.hub.filter.search': 'Search companies',
  'cases.hub.filter.suppliers': 'Suppliers',
  'cases.hub.filter.apply': 'Show these companies',
  'cases.hub.filter.loading': 'Loading…',
  'cases.hub.filter.error': "The companies can't be loaded right now.",
  'cases.hub.filter.none': 'No companies match',
  'cases.hub.filter.max': 'You can choose up to {n} companies',
  'search.start': 'Press Search in the menu to look up a PO number, invoice number, supplier or buyer',
  'search.none': 'No results',
  'search.error': 'Search unavailable',
  'search.loading': 'Searching…',
  'search.fileError': 'The original file could not be opened',
  'search.title': 'Results for “{q}”',
  'search.count': '{n} matches across cases, documents and companies',
  'search.count.one': '1 match across cases, documents and companies',
  'search.showing': 'Showing {shown} of {total}',
  'search.groupEmpty': 'No matches',
  'search.group.cases': 'Cases',
  'search.group.documents': 'Documents',
  'search.group.companies': 'Companies',
  'search.chip.attention': 'needs attention',
  'search.chip.done': 'done',
  'search.chip.matched': 'matched',
  'search.chip.open': 'open',
  'search.po': 'PO {n}',
  'search.invoiceRef': 'Invoice {n}',
  'search.doc.purchase_order': 'Purchase order',
  'search.doc.invoice': 'Invoice',
  'search.doc.delivery_note': 'Delivery note',
  'search.doc.packing_list': 'Packing list',
  'search.doc.packing_slip': 'Packing slip',
  'search.doc.document': 'Document',
  'search.cites': 'cites {n}',
  'search.supplier': 'Supplier',
  'search.customer': 'Customer',
  'search.case.one': '1 case',
  'search.case.many': '{n} cases',
  'cases.nt.back': 'Back to Home',
  'cases.nt.export': 'Export list',
  'cases.nt.marker': 'New today',
  'cases.nt.loading': 'Loading…',
  'cases.nt.empty': 'No cases',
  'cases.nt.error': 'The list can\'t be loaded right now.',
  'cases.nt.truncated': 'Showing the first {n} of {total} cases.',
  'cases.nt.od.invoice': 'Invoice arrived {n} days ago, still waiting for the delivery note',
  'cases.nt.od.delivery': 'Delivery note arrived {n} days ago, still waiting for the invoice',
  'cases.nt.od.generic': 'A document has been missing for {n} days',
  'cases.nt.od.noInvoice': 'Still waiting for the invoice after {n} days',
  'cases.nt.csv.order': 'Order',
  'cases.nt.csv.supplier': 'Supplier',
  'cases.nt.csv.buyer': 'Buyer',
  'cases.nt.csv.invoice': 'Invoice number',
  'cases.nt.csv.problem': 'Problem',
  'cases.nt.csv.description': 'Description',
  'cases.nt.csv.amount': 'Amount',
  'cases.nt.csv.currency': 'Currency',
  'cases.nt.csv.daysOpen': 'Days open',
  'cases.nt.csv.raisedOn': 'Raised on',
  'cases.hist.today': 'Today',
  'cases.hist.yesterday': 'Yesterday',
  'cases.hist.type.order': 'Order',
  'cases.hist.type.invoice': 'Invoice',
  'cases.hist.type.delivery_note': 'Delivery note',
  'cases.hist.type.packing_list': 'Packing list',
  'cases.hist.type.document': 'Document',
  'cases.hist.title.full': '{type} {number} from {supplier}',
  'cases.hist.title.noSupplier': '{type} {number}',
  'cases.hist.title.noNumber': '{type} from {supplier}',
  'cases.hist.state.noCase': 'No case',
  'cases.hist.state.discrepancy': 'Discrepancy: {reason}',
  'cases.hist.state.order_missing': 'Order missing',
  'cases.hist.state.matched': 'Matched',
  'cases.hist.state.done': 'Done',
  'cases.hist.state.waiting': 'Waiting',
  'cases.hist.reason.price': 'price differs',
  'cases.hist.reason.quantity': 'quantity differs',
  'cases.hist.reason.billed': 'billed above delivered',
  'cases.hist.reason.duplicate': 'duplicate invoice',
  'cases.hist.reason.vat': 'VAT error',
  'cases.hist.reason.look': 'needs a look',
  'cases.hist.filter.supplier': 'Supplier',
  'cases.hist.filter.type': 'Document type',
  'cases.hist.filter.allSuppliers': 'All suppliers',
  'cases.hist.filter.allDocuments': 'All documents',
  'cases.hist.filter.type.order': 'Orders',
  'cases.hist.filter.type.invoice': 'Invoices',
  'cases.hist.filter.type.delivery_note': 'Delivery notes',
  'cases.hist.filter.type.packing_list': 'Packing lists',
  'cases.hist.week.title': 'This week',
  'cases.hist.week.documents': 'Documents in',
  'cases.hist.week.documents.sub': 'All types',
  'cases.hist.week.discrepancy': 'Discrepancy',
  'cases.hist.week.discrepancy.sub': 'New disagreements',
  'cases.hist.week.late': 'Documents late',
  'cases.hist.week.late.sub': 'Became late this week',
  'cases.hist.week.error': 'The numbers can\'t be loaded right now.',
  'cases.hist.empty': 'No documents here.',
  'cases.hist.error': 'The history can\'t be loaded right now.',
  'case.back.attention': 'Back to Needs attention',
  'case.back.open': 'Back to Open',
  'case.back.matched': 'Back to Matched',
  'case.back.done': 'Back to Done',
  'case.back.cases': 'Back to Cases',
  'case.title.fallback': 'Case',
  'case.sub.opened': 'PO {po} · opened {date}',
  'case.sub.openedNoPo': 'Opened {date}',
  'case.sub.closed': 'PO {po} · closed {date}',
  'case.sub.closedNoPo': 'Closed {date}',
  'case.sub.received': 'Invoice {inv} · received {date}',
  'case.sub.receivedNoInv': 'Invoice received {date}',
  'case.sub.case': 'case {n}',
  'case.chip.attention': 'Needs attention',
  'case.chip.open': 'Open',
  'case.chip.matched': 'Matched',
  'case.chip.internal_po': 'Internal PO',
  'case.chip.low_confidence': 'Low confidence',
  'case.chip.done': 'Done',
  'case.card.order': 'Order',
  'case.card.confirmation': 'Confirmation',
  'case.card.delivery': 'Delivery',
  'case.card.invoice': 'Invoice',
  'case.card.paid': 'Paid',
  'case.status.matched': 'Matched',
  'case.status.differs': 'Differs',
  'case.status.missing': 'Missing',
  'case.status.received': 'Received',
  'case.status.waiting': 'Waiting',
  'case.status.not_due': 'Not due',
  'case.status.done': 'Done',
  'case.order.lines.one': '1 line · {total}',
  'case.order.lines.many': '{n} lines · {total}',
  'case.order.names': 'Invoice names PO {n}',
  'case.order.never': 'Never received',
  'case.order.internal': 'Internal PO · from invoice {n}',
  'case.problem.ipoTitle': 'Internal PO',
  'case.problem.ipoLabel': 'About this case',
  'case.problem.ipoTotal': 'Invoice total {total}',
  'case.status.internal_po': 'Not compared',
  'case.problem.ipoBody': 'The invoice {n} names no order, so Floxir made one from its lines. It is not checked against anything.',
  'case.problem.ipoBodyCited': 'The invoice names {po}, which never arrived. Floxir made an order from the invoice lines; nothing is checked until the real one comes.',
  'case.step.po_received': 'PO received',
  'case.step.invoice_received': 'Invoice received',
  'case.step.matched': 'Matched',
  'types.action.request_invoices': 'Request invoices',
  'case.confirmation.own': '{supplier}’ own number',
  'case.confirmation.ownNoSupplier': 'The supplier’s own number',
  'case.notReceived': 'Not received',
  'case.delivery.note': 'Delivery note {n}',
  'case.delivery.noteNoNumber': 'Delivery note',
  'case.delivery.packing': 'Packing list {n}',
  'case.delivery.packingNoNumber': 'Packing list',
  'case.delivery.pct': '{pct}% delivered · {date}',
  'case.delivery.pctNoDate': '{pct}% delivered',
  'case.delivery.on': 'Delivered {date}',
  'case.invoice.due': '{total} · due {date}',
  'case.invoice.total': '{total}',
  'case.invoice.more.one': '+1 more invoice',
  'case.invoice.more.many': '+{n} more invoices',
  'case.paid.notDue': 'Not due yet',
  'case.paid.waiting': 'Waiting for remittance',
  'case.paid.due': 'Due {date}',
  'case.paid.dueUnknown': 'Due date not on the invoice',
  'case.btn.formatted': 'Formatted',
  'case.btn.original': 'Original',
  'case.lines.title': 'Line by line',
  'case.lines.sub.full': 'Order, delivery and invoice side by side',
  'case.lines.sub.invoice_only': 'From the invoice only',
  'case.lines.sub.done': 'Order, delivery and invoice side by side',
  'case.lines.empty': 'There are no lines on this case yet.',
  'case.col.item': 'Item',
  'case.col.ordered': 'Ordered',
  'case.col.orderPrice': 'Order price',
  'case.col.delivered': 'Delivered',
  'case.col.invoiced': 'Invoiced',
  'case.col.invoicePrice': 'Invoice price',
  'case.col.difference': 'Difference',
  'case.col.creditNote': 'Credit note',
  'case.col.final': 'Final',
  'case.problem.difference': 'The difference',
  'case.problem.problem': 'The problem',
  'case.problem.standing': 'Where it stands',
  'case.problem.recovered': 'Recovered',
  'case.problem.price': 'Invoice price is above the agreed order price on {n} of {m} lines.',
  'case.problem.quantity': 'Billed quantity is above the order on {n} of {m} lines.',
  'case.problem.billed_over_delivered': 'Billed for more than was delivered on {n} of {m} lines.',
  'case.problem.priceDetail': '{supplier} billed {billed} each. The order said {agreed}.',
  'case.problem.duplicate': 'Same invoice {n} received twice.',
  'case.problem.duplicateNoNumber': 'The same invoice was received twice.',
  'case.problem.weak': 'Floxir isn\'t sure this invoice belongs to this order.',
  'case.problem.weakHint': 'Check the order and the invoice above.',
  'case.problem.omTitle': 'Order {po} never arrived',
  'case.problem.omTitleNoRef': 'An order never arrived',
  'case.problem.omBody': 'The invoice from {supplier} names this order, but Floxir has not received it.',
  'case.problem.omBodyNoRef': 'The invoice from {supplier} does not name an order number, and Floxir has no order for it.',
  'case.problem.omTotal': 'Invoice total {total} can\'t be checked until the order arrives.',
  'case.problem.waitingInvoice': 'Waiting for the invoice.',
  'case.problem.waitingDelivery': 'Waiting for the delivery note.',
  'case.problem.matched': 'The invoice agrees with the order. Waiting for the delivery note.',
  'case.problem.nothing': 'Nothing is missing.',
  'case.problem.accepted': 'Accepted',
  'case.problem.recoveredHint': 'Floxir doesn\'t read credit notes yet, so nothing is counted as recovered.',
  'case.problem.fallback': 'Needs a look',
  'case.status.placeholder': 'Placeholder',
  'case.order.placeholder': 'Placeholder from the invoice · Created by Floxir',
  'case.confirm.approve.title': 'Approve as is?',
  'case.confirm.approve.text': 'The difference stays on record. The case moves to Done and the invoice is marked approved.',
  'case.confirm.approve.yes': 'Approve',
  'case.confirm.reopen.title': 'Reopen this case?',
  'case.confirm.reopen.text': 'It goes back to the state its documents give.',
  'case.confirm.reopen.yes': 'Reopen',
  'case.confirm.create_from_invoice.title': 'Create a placeholder order?',
  'case.confirm.create_from_invoice.text': 'It is tagged “Created by Floxir”, never used to check this case, and replaced when the real order arrives.',
  'case.confirm.create_from_invoice.yes': 'Create placeholder',
  'case.confirm.cancel': 'Cancel',
  'case.action.err.closed': 'This case is already closed.',
  'case.action.err.notApprovable': "This case can't be approved.",
  'case.action.err.failed': "Couldn't save. Nothing was changed.",
  'case.action.request_correction_followup': 'Send follow-up',
  'case.action.request_order_followup': 'Send follow-up',
  'case.req.loading': 'Preparing the message…',
  'case.req.to': 'To',
  'case.req.subject': 'Subject',
  'case.req.message': 'Message',
  'case.req.from.invoice': 'From {supplier}’s last invoice',
  'case.req.from.supplier': 'From supplier details',
  'case.req.from.none': 'No address on file for {supplier}. Type one to send.',
  'case.req.unknown': 'This address isn’t one we have for {supplier}',
  'case.req.full': 'This will go to {address}',
  'case.req.test': 'Test mode: this message is recorded but not sent',
  'case.req.cancel': 'Cancel',
  'case.req.close': 'Close',
  'case.req.send': 'Send request',
  'case.req.sendFollowup': 'Send follow-up',
  'case.req.sending': 'Sending…',
  'case.req.recorded': 'Recorded, not sent. This is test mode.',
  'case.req.err.load': 'Couldn’t prepare the message.',
  'case.req.err.send': 'Couldn’t send the message.',
  'case.req.attach.title': 'Attachments',
  'case.req.attach.none': 'No files to attach.',
  'case.req.attach.total': 'Total {size} of 20 MB',
  'case.req.attach.size.unknown': 'size unknown',
  'case.req.attach.reason.missing': 'Original file not kept',
  'case.req.attach.reason.too_big': 'Too big (limit 10 MB per file)',
  'case.req.attach.reason.over_total': 'Would go over 20 MB',
  'case.req.attach.note': 'Added to the message when it is sent:',
  'case.req.err.unavailable': '{file} couldn’t be read. Untick it or try again. Nothing was sent.',
  'case.req.err.tooBig': '{file} is over the 10 MB limit. Untick it. Nothing was sent.',
  'case.req.err.totalBig': 'The files together are over 20 MB. Untick one. Nothing was sent.',
  'case.reqall.title': 'Request all from {supplier}',
  'case.reqall.orders': 'Orders',
  'case.reqall.counter': '{n} of {max} selected',
  'case.reqall.asked': 'Asked {date}',
  'case.reqall.noInvoiceFile': 'No invoice file',
  'case.reqall.more': '{n} more not included. Send this email, then request again.',
  'case.reqall.truncated': 'Only the first 300 are listed.',
  'case.reqall.attachInvoices': 'Attach the invoices',
  'case.reqall.empty': 'Nothing to request: this supplier has no missing orders in these dates.',
  'case.reqall.err.changed': 'The list changed (an order may have arrived). Nothing was sent.',
  'case.reqall.reload': 'Reload the list',
  'case.problem.weakEv.other': 'Invoice {inv} names order {ref}. This case’s order is {po}.',
  'case.problem.weakEv.none': 'Invoice {inv} names no order. This case’s order is {po}.',
  'case.problem.weakEv.this': 'Invoice {inv} names this order ({po}).',
  'case.confirm.confirm_order.title': 'Confirm this order?',
  'case.confirm.confirm_order.text': 'Floxir stops flagging this match. The case follows the normal rules from here.',
  'case.confirm.confirm_order.yes': 'Confirm',
  'case.confirm.not_this_order.title': 'Not this order?',
  'case.confirm.not_this_order.text': 'The invoice moves out of this case and becomes its own Order missing case. This case goes back to waiting for an invoice.',
  'case.confirm.not_this_order.textSome': 'The invoices that do not name this order move out, each to its own case. The others stay.',
  'case.confirm.not_this_order.yes': 'Move the invoice',
  'case.action.err.notWeak': 'This case is no longer a weak match.',
  'case.action.err.noInvoice': 'This case has no invoice.',
  'case.action.err.namesOrder': 'Every invoice on this case names this order.',
  'case.action.err.shared': 'This invoice is shared across several orders, so it can’t be moved here.',
  'case.next.button': 'Next case →',
  'case.next.position': 'New today · {n} of {total}',
  'case.next.last': 'That was the last case of the list.',
  'case.next.back': 'Back to the list',
  'cases.hist.filter.flagged': 'Flagged only',
  'cases.hist.emptyFlagged': 'No flagged documents.',
  'case.other.title': 'Attached but cite another order',
  'case.other.one': '1 invoice on this case cites a different order. It is not counted in this case’s amounts.',
  'case.other.many': '{n} invoices on this case cite a different order. They are not counted in this case’s amounts.',
  'case.other.row': 'Invoice {inv} · cites {ref}',
  'case.multi.title': 'Covers several orders',
  'case.multi.one': '1 invoice on this case covers several orders. Its total is not compared with this order alone; the line checks still apply.',
  'case.multi.many': '{n} invoices on this case cover several orders. Their totals are not compared with this order alone; the line checks still apply.',
  'case.multi.row': 'Invoice {inv} · covers {n} orders',
  'case.action.not_duplicate': 'Not a duplicate',
  'case.confirm.not_duplicate.title': 'Not a duplicate?',
  'case.confirm.not_duplicate.text': 'The invoice stays on this case as a normal invoice and counts in the totals. Floxir will not mark it as a duplicate again.',
  'case.confirm.not_duplicate.yes': 'Not a duplicate',
  'case.action.err.notCopy': 'No invoice on this case is marked as a copy.',
  'case.step.not_duplicate': 'Marked as not a duplicate',
  'case.actions.title': 'What do you want to do?',
  'case.action.request_correction': 'Request correction',
  'case.action.approve': 'Approve as is',
  'case.action.reopen': 'Reopen case',
  'case.action.request_order': 'Request order',
  'case.action.create_from_invoice': 'Create from invoice',
  'case.action.confirm_order': 'Confirm this order',
  'case.action.not_this_order': 'Not this order',
  'case.actions.hint.correction': 'Request correction drafts an email to {supplier} with the documents attached.',
  'case.actions.hint.order': 'Request order emails {supplier} for {po}. Create from invoice builds a placeholder order tagged "Created by Floxir", replaced when the real order arrives.',
  'case.actions.hint.orderNoRef': 'Request order emails {supplier} for the order. Create from invoice builds a placeholder order tagged "Created by Floxir", replaced when the real order arrives.',
  'case.actions.hint.weak': 'Confirm this order, or say it isn\'t this one.',
  'case.actions.hint.reopen': 'This case was approved as is. Reopen it only if a new document or a dispute arrives.',
  'case.actions.hint.closedAgreed': 'This case is closed because the documents agree.',
  'case.timeline.title': 'Case timeline',
  'case.step.discrepancy': 'Discrepancy',
  'case.step.opened': 'Opened',
  'case.step.correction_requested': 'Correction requested',
  'case.step.order_requested': 'Order requested',
  'case.step.invoice_requested': 'Invoice requested',
  'case.step.approved_as_is': 'Approved as is',
  'case.step.order_confirmed': 'Order confirmed',
  'case.step.order_rejected': 'Marked as not this order',
  'case.step.placeholder_created': 'Placeholder order created',
  'case.step.placeholder_replaced': 'Real order received',
  'case.step.reopened': 'Reopened',
  'case.step.credit_note': 'Credit note',
  'case.step.done': 'Done',
  'case.step.open': 'Open',
  'case.step.order_received': 'Order received',
  'case.step.checked': 'Checked',
  'case.stepText.reason.price': 'price above order',
  'case.stepText.reason.quantity': 'quantity differs',
  'case.stepText.reason.billed_over_delivered': 'billed above delivered',
  'case.stepText.reason.duplicate': 'duplicate invoice',
  'case.stepText.reason.vat_error': 'VAT error',
  'case.stepText.reason.weak_match': 'low match confidence',
  'case.stepText.reason.other': 'needs a look',
  'case.stepText.reason.order_missing': 'invoice names {po}, never received',
  'case.stepText.reason.order_missing_noref': 'invoice without an order, never received',
  'case.stepText.emailTo': 'email to {supplier}',
  'case.stepText.sentToday': 'Sent today',
  'case.stepText.sentOne': 'Sent 1 day ago',
  'case.stepText.sentMany': 'Sent {n} days ago',
  'case.stepText.by': 'by {who}',
  'case.stepText.notRequested': '—',
  'case.stepText.none': '—',
  'case.stepText.open': 'request the order or create it from the invoice',
  'case.stepText.agreed': 'documents agree',
  'case.stepText.accepted': 'approved as is',
  'case.stepText.estimated': 'estimated',
  'case.loading': 'Loading…',
  'case.error': 'The case can\'t be loaded right now.',
  'case.notFound': 'This case was not found.',
  ...reportsAgentEn,
  ...step5En,
};

export default en;
